import { Trans, useTranslation } from "react-i18next";
import type { Manual } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useShowError } from "../../error/ErrorProvider.js";
import { useManualStats, useTrashManual } from "./useManualMutations.js";

/**
 * "Move to the trash?" (ADR-056). A plain confirmation, because it can be undone, as opposed to the type-to-confirm
 * {@link DeleteManualDialog}. It names the linked copies, which go along, possibly from other facilities, so it waits for the
 * count; if that read fails it says so in general terms. A stale card ends in the shared error dialog, and the list refreshes.
 */
export function TrashManualDialog({
  manual,
  session,
  onClose,
  onTrashed,
}: {
  manual: Pick<Manual, "id" | "name" | "state" | "parentManualId">;
  session: AuthSession | null;
  onClose: () => void;
  onTrashed: (name: string) => void;
}) {
  const { t } = useTranslation();
  const trash = useTrashManual(session);
  const stats = useManualStats(session, manual.id);
  const showError = useShowError();
  const copies = stats.data?.liveLinkedChildCount ?? 0;

  const onConfirm = () => {
    if (trash.isPending) return;
    trash.mutate(
      { id: manual.id, expectedFrom: manual.state },
      {
        onSuccess: () => {
          onTrashed(manual.name);
          onClose();
        },
        onError: (err) => {
          showError(err);
          onClose();
        },
      },
    );
  };

  return (
    <Dialog isOpen isDismissable={!trash.isPending} onOpenChange={(open) => !open && !trash.isPending && onClose()}>
      <DialogTitle>{t("manualManagement.trash.title")}</DialogTitle>
      <DialogDescription>
        <Trans
          i18nKey={manual.parentManualId != null ? "manualManagement.trash.questionLinked" : "manualManagement.trash.question"}
          components={{ name: <strong className="text-foreground">{manual.name}</strong> }}
        />
      </DialogDescription>
      {copies > 0 ? <p className="text-muted-foreground text-sm">{t("manualManagement.trash.alsoCopies", { count: copies })}</p> : null}
      {stats.isError ? <p className="text-muted-foreground text-sm">{t("manualManagement.trash.alsoCopiesUnknown")}</p> : null}
      <DialogFooter>
        <Button variant="ghost" onPress={onClose} isDisabled={trash.isPending}>
          {t("manualManagement.misc.cancel")}
        </Button>
        <Button variant="destructive" onPress={onConfirm} isDisabled={trash.isPending || stats.isPending}>
          {t("manualManagement.trash.confirm")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
